import {
  Button,
  ButtonColorEnum,
  ClearIcon,
  ClockIcon,
  EditIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  PlusIcon,
  TrashDeleteIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect, useState } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Divider } from '../Divider/Divider';
import { CRONTAB_DAY_OF_WEEK_TO_DAY_LETTER } from '../Schedules/constants';

import { ActionWithDelivery } from './components/ActionWithDelivery/ActionWithDelivery';
import { ScheduleWithDeliveryItemForm } from './components/ScheduleWithDeliveryItemForm/ScheduleWithDeliveryItemForm';
import { ScheduleWithDeliveryItemFormContainer } from './components/ScheduleWithDeliveryItemFormContainer/ScheduleWithDeliveryItemFormContainer';
import { TimeFieldWithDelivery } from './components/TimeFieldWithDelivery/TimeFieldWithDelivery';
import { useDailySchedulesWithDeliveryForm } from './hooks/useDailySchedulesWithDeliveryForm';
import { useScheduleItemForm } from './hooks/useScheduleItemForm';
import { DailySchedulesWithDeliveryFormData, ScheduleWithDeliveryFormData } from './types';

type DailyWithDeliverySchedulesProps = {
  value?: DailySchedulesWithDeliveryFormData;
  onChange: (value: DailySchedulesWithDeliveryFormData) => void;
  isOptionalEndHour?: boolean;
};

export function DailySchedulesWithDelivery({
  onChange,
  value,
  isOptionalEndHour,
}: DailyWithDeliverySchedulesProps) {
  const { t } = useTranslation();
  const [openScheduleIndex, setOpenScheduleIndex] = useState<number | null>(null);
  const [isAddingSchedule, setIsAddingSchedule] = useState(true);

  const schedulesForm = useDailySchedulesWithDeliveryForm();

  const schedulesFormValues = useWatch({
    control: schedulesForm?.control,
    name: 'schedules',
  });

  useEffect(() => {
    if (schedulesFormValues.length !== 0) {
      setIsAddingSchedule(false);
    }
  }, [schedulesFormValues]);

  const newScheduleItemForm = useScheduleItemForm();

  const editScheduleItemForm = useScheduleItemForm();
  const {
    fields: schedules,
    remove: removeSchedule,
    append: appendSchedule,
    update: updateSchedule,
  } = useFieldArray({
    control: schedulesForm.control,
    name: 'schedules',
    rules: {
      required: t('El campo es obligatorio'),
    },
  });

  const handleRemoveSchedule = (index: number) => {
    removeSchedule(index);

    const updatedFormData = schedulesForm.getValues();
    onChange(updatedFormData);
  };

  const handleAppendSchedule = (scheduleFormData: ScheduleWithDeliveryFormData) => {
    appendSchedule(scheduleFormData);

    const updatedFormData = schedulesForm.getValues();
    onChange(updatedFormData);
  };

  const handleUpdateSchedule = (index: number, scheduleFormData: ScheduleWithDeliveryFormData) => {
    updateSchedule(index, scheduleFormData);

    const updatedFormData = schedulesForm.getValues();
    onChange(updatedFormData);
  };

  const handleRemoveAllSchedules = () => {
    schedulesForm.reset({ schedules: [] });
    onChange({ schedules: [] });
  };

  useEffect(() => {
    schedulesForm.reset(value);
  }, [schedulesForm.reset, value]);

  const handleAppendNewSchedule = newScheduleItemForm.handleSubmit(data => {
    handleAppendSchedule({
      ...data,
      id: uuidv4(),
    });
    setIsAddingSchedule(false);
    newScheduleItemForm.reset();
  });

  const handleOpenSchedule = (index: number) => {
    setIsAddingSchedule(false);

    if (openScheduleIndex !== index) {
      const schedule = schedules[index];
      editScheduleItemForm.reset(schedule);
      setOpenScheduleIndex(index);
    } else {
      setOpenScheduleIndex(null);
    }
  };

  const editScheduleItemFormData = useWatch({
    control: editScheduleItemForm.control,
  });

  useEffect(() => {
    if (openScheduleIndex === null) {
      return;
    }

    const schedule = schedules[openScheduleIndex];
    handleUpdateSchedule(openScheduleIndex, {
      ...schedule,
      ...editScheduleItemFormData,
      id: schedule.id,
    });
  }, [editScheduleItemFormData, openScheduleIndex]);

  return (
    <>
      <div className="flex w-full items-center justify-between">
        <Typography fontSize={FontSizeEnum.Xl} weight={FontWeightEnum.Semibold}>
          {t('Lista de horarios')}
        </Typography>

        <div className="mt-2 flex items-center justify-end gap-1">
          <Typography fontSize={FontSizeEnum.Xs} weight={FontWeightEnum.Semibold}>
            {t('Nuevo')}
          </Typography>

          <IconButton
            size={IconButtonSizeEnum.SM}
            onClick={() => {
              setOpenScheduleIndex(null);
              setIsAddingSchedule(true);
            }}
          >
            <PlusIcon size={IconSizeEnum.XS} />
          </IconButton>

          <IconButton
            className="ml-1"
            color={IconButtonColorEnum.DEFAULT_REVERSE}
            size={IconButtonSizeEnum.SM}
            onClick={handleRemoveAllSchedules}
          >
            <ClearIcon size={IconSizeEnum.XS} />
          </IconButton>
        </div>
      </div>

      <div className="w-full">
        <div className="flex flex-col gap-2">
          {schedules.map((schedule, index) => {
            const isEditingSchedule = openScheduleIndex === index;
            const handleOpen = () => {
              handleOpenSchedule(index);
            };
            const handleDelete = () => {
              handleRemoveSchedule(index);
            };

            return (
              <div key={index} className="w-full">
                <div className="flex w-full">
                  <div className="flex w-fit">
                    <ClockIcon className="mr-1 mt-2" size={IconSizeEnum.SM} />
                  </div>
                  <div className="flex w-full flex-col gap-1">
                    <button
                      className="flex w-full rounded-size-8 bg-[black]/[.02] p-1"
                      onClick={handleOpen}
                      type="button"
                    >
                      <div className="flex w-1/2 flex-col items-start">
                        <TimeFieldWithDelivery label={t('Inicio')} time={schedule.startHour} />

                        <TimeFieldWithDelivery label={t('Fin')} time={schedule.endHour} />
                      </div>

                      <div className="flex w-1/2 items-start gap-1">
                        <Typography color={FontColorEnum.Medium20} weight={FontWeightEnum.Semibold}>
                          {t('Repetición') + ':'}
                        </Typography>
                        <Typography color={FontColorEnum.Medium20}>
                          {schedule.repetitionDayOfWeek
                            .sort((dayA, dayB) => dayA.localeCompare(dayB))
                            .map(dayOfWeek => CRONTAB_DAY_OF_WEEK_TO_DAY_LETTER[dayOfWeek])
                            .join(',')}
                        </Typography>
                      </div>
                    </button>
                    <div className="flex w-full rounded-size-8 bg-[black]/[.02] p-1">
                      <TimeFieldWithDelivery
                        label={t('Entrega')}
                        time={schedule.deliveryHour}
                        classname="flex-1"
                      />
                      <TimeFieldWithDelivery
                        label={t('Asignación')}
                        time={schedule.assignment_hour}
                        classname="flex-1"
                      />
                    </div>
                  </div>

                  <div className="ml-1 flex gap-1">
                    <ActionWithDelivery icon={EditIcon} onClick={handleOpen} />

                    <ActionWithDelivery icon={TrashDeleteIcon} onClick={handleDelete} />
                  </div>
                </div>

                {isEditingSchedule && (
                  <ScheduleWithDeliveryItemFormContainer>
                    <ScheduleWithDeliveryItemForm
                      form={editScheduleItemForm}
                      startHourInputName="startHour"
                      endHourInputName="endHour"
                      repetitionDayOfWeek="repetitionDayOfWeek"
                      assignment_hour="assignment_hour"
                      deliveryHour="deliveryHour"
                      estimation_time="estimation_time"
                      isOptionalEndHour={isOptionalEndHour}
                    />
                  </ScheduleWithDeliveryItemFormContainer>
                )}
              </div>
            );
          })}
        </div>

        {isAddingSchedule && (
          <div>
            <Divider className="mt-4" />

            <ScheduleWithDeliveryItemFormContainer>
              <div className="mb-3">
                <Typography color={FontColorEnum.Medium20} weight={FontWeightEnum.Bold}>
                  {t('Nueva')}
                </Typography>
              </div>

              <ScheduleWithDeliveryItemForm
                form={newScheduleItemForm}
                startHourInputName="startHour"
                endHourInputName="endHour"
                repetitionDayOfWeek="repetitionDayOfWeek"
                assignment_hour="assignment_hour"
                deliveryHour="deliveryHour"
                estimation_time="estimation_time"
                isOptionalEndHour={isOptionalEndHour}
              />

              <div className="flex w-full justify-end gap-1">
                <Button
                  color={ButtonColorEnum.GHOST}
                  onClick={() => {
                    setIsAddingSchedule(false);
                  }}
                >
                  <Typography weight={FontWeightEnum.Semibold}>{t('Cancelar')}</Typography>
                </Button>

                <Button color={ButtonColorEnum.DEFAULT_GHOST} onClick={handleAppendNewSchedule}>
                  {<Typography weight={FontWeightEnum.Semibold}>{t('Crear')}</Typography>}
                </Button>
              </div>
            </ScheduleWithDeliveryItemFormContainer>
          </div>
        )}

        {schedules.length > 0 && <Divider className="my-4" />}
      </div>
    </>
  );
}
